import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormGroup
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  LocationOn as LocationOnIcon,
  MyLocation as MyLocationIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Diamond as DiamondIcon,
  LocalOffer as CouponIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  AccessTime as AccessTime,
  Home as HomeIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  ContentCopy as CopyIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useTimer } from '../contexts/TimerContext';
import { useAuth } from '../contexts/AuthContextNew';
import EnhancedLocationDialog from '../components/EnhancedLocationDialog';
import BottomNavigation from '../components/BottomNavigation';
import { walletAPI, paymentAPI, locationAPI, bookingAPI, serviceBookingAPI, providerAPI } from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import useResponsive from '../hooks/useResponsive';
import { createStripePromise } from '../utils/stripeUtils';
import { formatCurrency, getCountryCode, getCountryFields } from '../utils/countryUtils';
import { formatDateShort } from '../utils/timezoneUtils';
import { computeServiceFeeAmount } from '../constants/serviceFee';

// Initialize Stripe using utility function (no hardcoded fallback)
const stripePromise = createStripePromise();

// Payment Form Component (copied from ProductCheckout)
const PaymentForm = ({ 
  amount, 
  onSuccess, 
  onError, 
  onCancel,
  grandTotal,
  subTotal,
  serviceFeeAmount,
  tipAmount,
  couponCode,
  couponDiscount,
  appliedBalance,
  appliedGems,
  savedCards,
  selectedCard,
  setSelectedCard,
  // Booking data
  bookingData,
  // Reschedule data
  isReschedule,
  originalBooking
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const countryCode = getCountryCode(); // Get country code for currency formatting
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveCard, setSaveCard] = useState(true);


  const handlePayment = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Stop the global timer immediately when payment processing starts
    if (window.stopGlobalTimer) {
      window.stopGlobalTimer();
     // console.log('🛑 Timer stopped - payment processing started');
    }

    setLoading(true);
    setError('');

    try {
      let paymentMethodId = selectedCard;

      // If no saved card is selected, create a new payment method
      if (!selectedCard) {
        // Submit the form to validate all fields
        const { error: submitError } = await elements.submit();
        if (submitError) {
          throw new Error(submitError.message || 'Please check your payment details');
        }

        // Create payment method
        const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name: 'Customer',
              email: 'customer@example.com',
            },
          },
        });

        if (paymentMethodError) {
          throw new Error(paymentMethodError.message || 'Failed to create payment method');
        }

        paymentMethodId = paymentMethod.id;
      }

      // Process service booking payment
      // PRODUCTION: Using production URL - localhost fallback commented out
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/customer/service-booking-payments/process-service-booking`, { // COMMENTED FOR PRODUCTION
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/customer/service-booking-payments/process-service-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('customerToken')}`,
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100),
          currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
          paymentMethodId: paymentMethodId,
          saveCard: saveCard && !selectedCard, // Only save if not using saved card
          bookingData: bookingData, // Include all booking data
          isReschedule: isReschedule || false, // Flag to indicate reschedule operation
          originalBookingId: originalBooking?.id || null, // Original booking ID for reschedule
          // Include coupon and wallet data
          couponCode: couponCode || null,
          couponDiscount: couponDiscount || 0,
          appliedWalletBalance: appliedBalance || 0,
          appliedGems: appliedGems || 0
        }),
      });

      if (!response.ok) {
        throw new Error(`Payment failed (${response.status}): ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Payment failed');
      }

      // Call success handler with payment result
      onSuccess?.(result.data);

    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
      onError?.(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePayment} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Error Messages */}
      {error && (
        <div style={{
          color: '#ff3b30',
          fontSize: '14px',
          margin: 0,
          padding: '8px 12px',
          background: '#fff5f5',
          borderRadius: '6px',
          border: '1px solid #fed7d7'
        }}>
          {error}
        </div>
      )}

      {/* Saved Payment Methods */}
      {savedCards && savedCards.length > 0 && (
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            💳 Saved Payment Methods
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            {savedCards.map((card) => (
              <div 
                key={card.id} 
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'white',
                  borderRadius: '10px',
                  border: `2px solid ${selectedCard === card.id ? '#3b82f6' : '#e2e8f0'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSelectedCard(card.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Radio
                    checked={selectedCard === card.id}
                    onChange={() => setSelectedCard(card.id)}
                    value={card.id}
                    size="small"
                    sx={{
                      color: '#3b82f6',
                      '&.Mui-checked': {
                        color: '#3b82f6',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 16,
                      },
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 500, color: '#374151' }}>
                      •••• •••• •••• {card.last4 || '****'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Expires {card.expMonth}/{card.expYear}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    padding: '4px 8px',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                    {card.brand?.toUpperCase() || 'VISA'}
                  </span>
                  {card.isDefault && (
                    <span style={{
                      padding: '4px 8px',
                      background: '#10b981',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      Default
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <button
            type="button"
            onClick={() => setSelectedCard(null)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px dashed #3b82f6',
              borderRadius: '8px',
              background: 'transparent',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#eff6ff';
              e.target.style.borderColor = '#1d4ed8';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = '#3b82f6';
            }}
          >
            + Use New Payment Method
          </button>
        </div>
      )}

      {/* Payment Summary */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>
          Payment Summary
        </h3>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ color: '#64748b' }}>Subtotal:</span>
          <span style={{ fontWeight: '500' }}>{formatCurrency(subTotal)}</span>
        </div>
        
        {couponDiscount > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '8px',
            padding: '8px',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
            border: '1px solid #c3e6cb'
          }}>
            <span style={{ color: '#155724', fontWeight: '500', fontSize: '14px' }}>
              🎉 Coupon Discount ({couponCode})
            </span>
            <span style={{ fontWeight: '600', color: '#155724', fontSize: '14px' }}>
              -{formatCurrency(couponDiscount)}
            </span>
          </div>
        )}

        {(serviceFeeAmount ?? 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>Service fee:</span>
            <span style={{ fontWeight: '500' }}>{formatCurrency(serviceFeeAmount)}</span>
          </div>
        )}
        
        {tipAmount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>Tip:</span>
            <span style={{ fontWeight: '500' }}>{formatCurrency(tipAmount)}</span>
          </div>
        )}
        
        {appliedBalance > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>Wallet Applied:</span>
            <span style={{ fontWeight: '500', color: '#10b981' }}>-{formatCurrency(appliedBalance)}</span>
          </div>
        )}
        
        {appliedGems > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>Gems Applied ({appliedGems} gems):</span>
            <span style={{ fontWeight: '500', color: '#10b981' }}>-{formatCurrency((appliedGems / 1000) * 10)}</span>
          </div>
        )}
        
        <div style={{ 
          borderTop: '1px solid #e2e8f0', 
          paddingTop: '12px', 
          marginTop: '12px',
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>Total:</span>
          <span style={{ color: '#3b82f6', fontSize: '20px', fontWeight: '700' }}>
            {formatCurrency(grandTotal)}
          </span>
        </div>
      </div>

      {/* Payment Element - Only show when no saved card is selected */}
      {!selectedCard && (
        <div style={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {savedCards.length > 0 ? '💳 Add New Payment Method' : '💳 Payment Details'}
          </h3>
          <PaymentElement 
            options={{
              layout: 'tabs',
              paymentMethodOrder: [
                'card',
                'apple_pay',
                'google_pay',
                'afterpay_clearpay',
                'klarna',
                'alipay',
                'zip',
                'paypal',
                'link'
              ],
              defaultValues: {
                billingDetails: {
                  name: 'Customer',
                  email: 'customer@example.com',
                }
              },
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'auto',
                  phone: 'auto',
                  address: 'auto'
                }
              }
            }}
          />
          
          {/* Save Card Option */}
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '10px',
            border: '1px solid #bbf7d0',
            position: 'relative',
            zIndex: 1000,
            marginTop: '20px',
            pointerEvents: 'auto'
          }}>
            <label 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                userSelect: 'none',
                pointerEvents: 'auto'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSaveCard(!saveCard);
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={saveCard}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSaveCard(e.target.checked);
                    }}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#16a34a',
                        '&:hover': {
                          backgroundColor: 'rgba(22, 163, 74, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#16a34a',
                      },
                    }}
                  />
                }
                label=""
                sx={{ margin: 0 }}
              />
              <span 
                style={{
                  fontSize: '14px',
                  color: '#166534',
                  fontWeight: 500,
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 1001,
                  pointerEvents: 'auto'
                }}
              >
                💾 Save this payment method for future purchases
              </span>
            </label>
          </div>
          
          {/* Security Notice */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            borderRadius: '16px',
            border: '2px solid #bbf7d0',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.1)',
            marginTop: '20px'
          }}>
            <span style={{
              fontSize: '24px',
              marginRight: '12px',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
            }}>
              🔒
            </span>
            <p style={{
              fontSize: '16px',
              color: '#166534',
              margin: 0,
              fontWeight: 600
            }}>
              Your payment information is secure and encrypted
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            background: '#ffffff',
            color: '#64748b',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: loading ? 0.6 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#475569';
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#64748b';
            }
          }}
        >
          Cancel
        </button>
        
        <button
          type="submit"
          disabled={loading || !stripe}
          style={{
            flex: 2,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '12px',
            background: loading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
            opacity: loading ? 0.6 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
            }
          }}
        >
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Processing...
            </div>
          ) : (
            `Pay ${formatCurrency(grandTotal)}`
          )}
        </button>
      </div>

      {/* Security Notice */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        marginTop: '16px'
      }}>
        <LockIcon style={{ color: '#0ea5e9', fontSize: '16px' }} />
        <span style={{ color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
          Your payment information is secure and encrypted
        </span>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider: initialProvider, cartItems, selectedSlot, isReschedule, originalBooking, rescheduleFee, rescheduleRule } = location.state || {};
  const { clearProviderCart } = useCart();
  const { timerState, stopGlobalTimer, resetGlobalTimer } = useTimer();
  const { user } = useAuth();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  
  const normalizeQuantity = (value) => {
    if (value === null || value === undefined) return 1;
    const numeric = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
  };

  const getItemQuantity = (item) => normalizeQuantity(item?.quantity);

  const parsePriceValue = (price) => {
    if (price === null || price === undefined) return 0;
    if (typeof price === 'number') return Number.isFinite(price) ? price : 0;
    if (typeof price === 'string') {
      const cleaned = price.replace(/[^0-9.\-]/g, '');
      const parsed = parseFloat(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const parseDurationValue = (duration, fallback = 60) => {
    if (duration === null || duration === undefined) return fallback;
    if (typeof duration === 'number') return Number.isFinite(duration) ? duration : fallback;
    if (typeof duration === 'string') {
      const parsed = parseInt(duration.replace(/[^0-9\-]/g, ''), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }
    return fallback;
  };

  const expandedCartItems = useMemo(() => {
    if (!Array.isArray(cartItems)) return [];
    return cartItems.flatMap((item) => {
      const quantity = getItemQuantity(item);
      const unitDuration = parseDurationValue(item?.duration);
      const unitPrice = parsePriceValue(item?.price);
      return Array.from({ length: quantity }).map((_, index) => ({
        ...item,
        quantity: 1,
        originalQuantity: quantity,
        quantityIndex: index + 1,
        expandedId: `${item?.cartItemId || item?._id || item?.id || 'service'}-${index + 1}`,
        unitDuration,
        unitPrice,
      }));
    });
  }, [cartItems]);

  const computeTotalPrice = useCallback(() => {
    return expandedCartItems.reduce((sum, item) => sum + (item.unitPrice ?? parsePriceValue(item.price)), 0);
  }, [expandedCartItems]);

  const computeTotalDuration = useCallback(() => {
    return expandedCartItems.reduce((sum, item) => sum + (item.unitDuration ?? parseDurationValue(item.duration)), 0);
  }, [expandedCartItems]);

  const buildServicePayload = useCallback(() => {
    return expandedCartItems.map((item, index) => ({
      serviceId: item.id,
      serviceName: item.name,
      quantity: 1,
      price: item.unitPrice ?? parsePriceValue(item.price),
      duration: item.unitDuration ?? parseDurationValue(item.duration),
      color: item.color || item.serviceColor || '#ff9800',
      providerId: item.providerId,
      providerName: item.providerName,
      quantityIndex: item.quantityIndex,
      originalQuantity: item.originalQuantity,
      sequence: index + 1,
      originalCartItemId: item.cartItemId || item._id || item.id,
    }));
  }, [expandedCartItems]);

  const buildServicesWithColors = useCallback(() => {
    const occurrenceCounter = {};
    return buildServicePayload().map((service) => {
      const occurrenceIndex = occurrenceCounter[service.serviceId] || 0;
      occurrenceCounter[service.serviceId] = occurrenceIndex + 1;

      let actualColor = service.color || '#1976d2';

      if (selectedSlot && selectedSlot.slots && selectedSlot.slots.length > 0) {
        const firstSlot = selectedSlot.slots[0];
        if (firstSlot.schedule && firstSlot.schedule.length > 0) {
          const matchingServices = firstSlot.schedule.filter(s => s.serviceId === service.serviceId);
          const allocation = matchingServices[Math.min(occurrenceIndex, matchingServices.length - 1)];
          if (allocation && allocation.serviceColor) {
            actualColor = allocation.serviceColor;
          }
        }
      }

      return {
        ...service,
        color: actualColor
      };
    });
  }, [buildServicePayload, selectedSlot]);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Debug: Log the selectedSlot data immediately after extraction
  // console.log('🔍 [CHECKOUT INIT] SelectedSlot data received from navigation:', {
  //   selectedSlotExists: !!selectedSlot,
  //   selectedSlot: selectedSlot,
  //   selectedSlotStaffAllocation: selectedSlot?.staffAllocation,
  //   selectedSlotSlots: selectedSlot?.slots,
  //   firstSlotStaffAllocation: selectedSlot?.slots?.[0]?.staffAllocation,
  //   cartItemsCount: cartItems?.length || 0,
  //   providerId: initialProvider?.id
  // });
  
  // Local state for enhanced provider data
  const [provider, setProvider] = useState(initialProvider);

  // State management
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletGems, setWalletGems] = useState(0);
  const [appliedBalance, setAppliedBalance] = useState(0);
  const [appliedGems, setAppliedGems] = useState(0);
  const [paymentOption, setPaymentOption] = useState('none');
  const [grandTotal, setGrandTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [serviceAddress, setServiceAddress] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [showTimerExpiredDialog, setShowTimerExpiredDialog] = useState(false);
  const [allLogs, setAllLogs] = useState([]);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [saveCard, setSaveCard] = useState(true);
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);

  // Tip options
  const tipOptions = [0, 5, 10, 15, 20, 25, 30];

  // Function to add logs to the collection
  const addLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      message,
      data: data ? JSON.stringify(data, null, 2) : null
    };
    setAllLogs(prev => [...prev, logEntry]);
  //  console.log(`[${timestamp}] ${message}`, data);
  };

  // Function to copy all logs to clipboard
  const copyAllLogs = async () => {
    try {
      let logsText = allLogs.map(log => 
        `[${log.timestamp}] ${log.message}${log.data ? '\n' + JSON.stringify(log.data, null, 2) : ''}`
      ).join('\n\n');
      
      // Add current booking data if available
      if (selectedSlot) {
        logsText += '\n\n=== CURRENT BOOKING DATA ===\n';
        logsText += `Selected Slot: ${JSON.stringify(selectedSlot, null, 2)}\n\n`;
        
        if (cartItems && cartItems.length > 0) {
          logsText += `Cart Items: ${JSON.stringify(cartItems, null, 2)}\n\n`;
        }
        
        // Add staff allocation info
        const finalStaffAllocation = selectedSlot?.staffAllocation || (selectedSlot?.slots && selectedSlot.slots.length > 0 ? selectedSlot.slots[0].staffAllocation : []) || [];
        logsText += `Staff Allocation: ${JSON.stringify(finalStaffAllocation, null, 2)}\n\n`;
      }
      
      await navigator.clipboard.writeText(logsText);
      
      // Show success message with more details
      const logCount = allLogs.length;
      alert(`✅ Success! Copied ${logCount} log entries + current booking data to clipboard.\n\nYou can now paste them in the chat to help with debugging.`);
    } catch (error) {
      console.error('Failed to copy logs:', error);
      alert('❌ Failed to copy logs to clipboard. Please try again.');
    }
  };

  // Function to copy payment data that will be sent to backend
  const copyPaymentData = async () => {
    try {
      if (!selectedSlot || !cartItems) {
        alert('❌ No booking data available to copy.');
        return;
      }

      // Calculate the same data that would be sent to payment processing
      const finalStaffAllocation = selectedSlot?.staffAllocation || (selectedSlot?.slots && selectedSlot.slots.length > 0 ? selectedSlot.slots[0].staffAllocation : []) || [];
      
      const bookingData = {
        // Basic booking info
        providerId: provider.id,
        date: selectedSlot.date,
        startTime: selectedSlot.time,
        duration: computeTotalDuration(),
        serviceType: provider?.type === 'Store' ? (provider?.providerType === 'salon' ? 'In Salon' : 'At Home') : (provider?.type === 'Pros' ? 'At Home' : 'In Salon'),
        teamMember: selectedSlot.teamMember,
        
        // Services
        services: buildServicePayload(),
        
        // Staff allocation
        staffAllocation: finalStaffAllocation,
        
        // Reservation info
        reservationId: selectedSlot?.reservationId,
        
        // Additional details
        specialInstructions: specialInstructions,
        providerName: provider.name,
        providerType: provider.type,
        providerAddress: provider.address || provider.fullAddress
      };

      const paymentData = {
        amount: Math.round(grandTotal * 100), // Convert to cents
        currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
        orderType: 'service',
        bookingData: bookingData,
        isReschedule: isReschedule || false,
        originalBookingId: originalBooking?.id || null
      };

      const paymentDataText = `=== PAYMENT DATA TO BE SENT TO BACKEND ===\n\n${JSON.stringify(paymentData, null, 2)}\n\n=== BREAKDOWN ===\n\nBooking Data:\n${JSON.stringify(bookingData, null, 2)}\n\nStaff Allocation Details:\n${JSON.stringify(finalStaffAllocation, null, 2)}`;
      
      await navigator.clipboard.writeText(paymentDataText);
      
      alert(`✅ Success! Copied payment data to clipboard.\n\nThis shows exactly what data will be sent to the payment processing endpoint.`);
    } catch (error) {
      console.error('Failed to copy payment data:', error);
      alert('❌ Failed to copy payment data to clipboard. Please try again.');
    }
  };

  useEffect(() => {
    if (!initialProvider || !cartItems || !selectedSlot) {
      navigate('/home');
      return;
    }
    
    // Validate that we have proper staff allocation data
    const finalStaffAllocation = selectedSlot?.staffAllocation || (selectedSlot?.slots && selectedSlot.slots.length > 0 ? selectedSlot.slots[0].staffAllocation : []) || [];
    
    if (!finalStaffAllocation || !Array.isArray(finalStaffAllocation) || finalStaffAllocation.length === 0) {
      console.error('❌ [CHECKOUT INIT] ERROR: No valid staff allocation found in selectedSlot');
      console.error('🔍 [CHECKOUT INIT] This indicates a system issue - staff allocation should be provided from availability system');
      console.error('🔍 [CHECKOUT INIT] SelectedSlot data:', selectedSlot);
      
      // Show error to user and redirect back
      alert('Error: No staff allocation found. This indicates a system issue. Please try selecting the time slot again.');
      navigate('/home');
      return;
    }
    
  //  console.log('✅ [CHECKOUT INIT] Valid staff allocation found:', finalStaffAllocation);

    addLog('Checkout page initialized', {
      provider: provider?.id,
      cartItems: cartItems.length,
      cartItemsData: cartItems,
      selectedSlot: selectedSlot?.date + ' ' + selectedSlot?.time,
      isReschedule,
      originalBooking: originalBooking?.id,
      rescheduleFee
    });

    // Fetch minimal provider details (phone and address only)
    const fetchMinimalProviderDetails = async () => {
      try {
        addLog('Fetching minimal provider details for ID:', initialProvider.id);
        const providerData = await providerAPI.getProvider(initialProvider.id);
        
        // Extract only the minimal fields we need
        const actualProviderData = providerData.data || providerData;
        
        addLog('Provider API response received', {
          responseData: providerData,
          actualProviderData: actualProviderData,
          phone: actualProviderData.phone,
          phoneNumber: actualProviderData.phoneNumber,
          address: actualProviderData.address,
          fullAddress: actualProviderData.fullAddress,
          providerType: actualProviderData.providerType,
          type: actualProviderData.type
        });
        
        // Update provider with minimal required fields
        setProvider(prevProvider => ({
          ...prevProvider,
          name: cartItems[0]?.providerName || prevProvider.name, // Use name from cart items
          phone: actualProviderData.phone || actualProviderData.phoneNumber,
          address: actualProviderData.address || actualProviderData.fullAddress, // Provider address (for Salon)
          providerType: actualProviderData.category || actualProviderData.providerType || actualProviderData.type // Use category field for salon/pros detection
        }));
        
        addLog('Provider state updated', {
          name: cartItems[0]?.providerName,
          phone: actualProviderData.phone || actualProviderData.phoneNumber,
          address: actualProviderData.address || actualProviderData.fullAddress,
          providerType: actualProviderData.category || actualProviderData.providerType || actualProviderData.type,
          category: actualProviderData.category,
          originalType: actualProviderData.type
        });
      } catch (error) {
        console.warn('Failed to fetch minimal provider details:', error.message);
        addLog('Provider API call failed', { error: error.message, providerId: initialProvider.id });
        // Use cart item provider name as fallback
        setProvider(prevProvider => ({
          ...prevProvider,
          name: cartItems[0]?.providerName || prevProvider.name
        }));
      }
    };

    fetchMinimalProviderDetails();

    // Debug team member information
    addLog('Team member information received', {
      selectedSlot: selectedSlot,
      teamMember: selectedSlot?.teamMember,
      teamMemberId: selectedSlot?.teamMember?.id,
      teamMemberName: selectedSlot?.teamMember?.name,
      teamMemberType: typeof selectedSlot?.teamMember,
      teamMemberIdType: typeof selectedSlot?.teamMember?.id
    });

    // Debug service information
    // Extract service colors from selectedSlot schedule
    const servicesWithActualColors = cartItems.map(item => {
      let actualColor = item.color || '#1976d2'; // Default color
      
      // Try to find the actual color from the slot schedule
      if (selectedSlot && selectedSlot.slots && selectedSlot.slots.length > 0) {
        const firstSlot = selectedSlot.slots[0];
        if (firstSlot.schedule && firstSlot.schedule.length > 0) {
          const serviceInSchedule = firstSlot.schedule.find(s => s.serviceId === item.id);
          if (serviceInSchedule && serviceInSchedule.serviceColor) {
            actualColor = serviceInSchedule.serviceColor;
          }
        }
      }
      
      return {
        serviceId: item.id,
        serviceName: item.name,
        serviceColor: actualColor,
        hasColor: actualColor !== '#1976d2'
      };
    });

    addLog('Service information received', {
      cartItems: cartItems,
      cartItemsCount: cartItems.length,
      expandedCartItemsCount: expandedCartItems.length,
      services: buildServicePayload(),
      servicesWithColors: servicesWithActualColors,
      selectedSlotSchedule: selectedSlot?.slots?.[0]?.schedule || []
    });

    initializeCheckout();
    fetchWalletBalance();
    addLog('About to fetch saved cards...');
    fetchSavedCards();
  }, []);

  const serviceFeeAmount = useMemo(
    () => computeServiceFeeAmount(subTotal, couponDiscount, isReschedule, rescheduleFee),
    [subTotal, couponDiscount, isReschedule, rescheduleFee]
  );

  useEffect(() => {
    calculateGrandTotal();
  }, [subTotal, tipAmount, couponDiscount, appliedBalance, appliedGems, paymentOption, isReschedule, rescheduleFee, serviceFeeAmount]);

  // Check for timer expiration and redirect to availability page
  useEffect(() => {
    // Only check for timer expiration if we're not in reschedule mode, not processing payment, and we have a provider
    if (!isReschedule && !isProcessing && provider?.id && !timerState.isVisible) {
      console.log('⏰ Timer expired - showing dialog and redirecting to availability page');
      
      // Show dialog to user
      setShowTimerExpiredDialog(true);
      
      // Auto-redirect after 3 seconds
      setTimeout(() => {
        setShowTimerExpiredDialog(false);
        navigate(`/availability/${provider.id}`, {
          state: {
            provider: provider,
            cartItems: cartItems,
            fromCheckout: true
          }
        });
      }, 3000);
    }
  }, [timerState.isVisible, isReschedule, isProcessing, provider?.id, navigate, cartItems]);

  // Monitor payment form dialog state
  useEffect(() => {
    addLog('Payment form dialog state changed', { showPaymentForm });
  }, [showPaymentForm]);

  const initializeCheckout = () => {
    // Calculate subtotal from cart items, factoring in quantity
    const total = computeTotalPrice();
    setSubTotal(total);

    // Set service address based on provider type
    if (!isSalonProvider()) {
      // For Pros (At Home), use the service address from availability
      setServiceAddress(selectedSlot?.serviceAddress || '');
    } else {
      // For Salon (In Salon), use the provider address or show a default message
      const salonAddress = selectedSlot?.providerAddress || provider?.address || provider?.location;
      setServiceAddress(salonAddress || 'Salon location will be provided after booking');
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await walletAPI.getWalletBalance();
      if (response.success) {
        setWalletBalance(response.data.balance || 0);
        setWalletGems(response.data.gems || 0);
      }
    } catch (error) {
      console.error('Error loading wallet balance:', error);
      // Set default values on error
      setWalletBalance(0);
      setWalletGems(0);
    }
  };

  const fetchSavedCards = async () => {
    try {
      addLog('Loading real Stripe saved payment methods...');
      
      const token = localStorage.getItem('customerToken');
      if (!token) {
        addLog('No customer token found for fetching saved cards');
        setSavedCards([]);
        return;
      }

      // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/payments/saved-methods`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/customer/payments/saved-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const cards = data.paymentMethods || [];
        setSavedCards(cards);
        addLog('Real saved cards loaded', { 
          count: cards.length, 
          cards: cards,
          responseData: data,
          hasPaymentMethods: !!data.paymentMethods
        });
        
        // Don't select any card by default - let user choose
        setSelectedCard(null);
        addLog('No card selected by default', { cardCount: cards.length });
      } else {
        addLog('Failed to load saved cards', { 
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });
        setSavedCards([]);
      }
    } catch (error) {
      console.error('Error loading saved cards:', error);
      addLog('Error loading saved cards', { error: error.message });
      setSavedCards([]);
    }
  };
  
  // Get country code for currency formatting
  const countryCode = getCountryCode();
  
  // Helper function to format price properly
  const formatPrice = (price) => {
    if (!price) return formatCurrency(0);
    
    // If price is already a string with currency symbol, return as is
    if (typeof price === 'string' && (price.includes('$') || price.includes('₹'))) {
      return price;
    }
    
    // Convert to number and format with currency
    const numPrice = typeof price === 'string' 
      ? parseFloat(price.replace(/[$,₹]/g, '')) 
      : parseFloat(price) || 0;
    
    return formatCurrency(numPrice);
  };

  // Helper function to get correct provider type
  const getProviderType = () => {
    // Use the same logic as ProviderProfile page
    // Backend returns providerType field for salon/pros distinction
    return provider?.type === 'Store' 
      ? (provider?.providerType === 'salon' ? 'Salon' : 'Pros') 
      : provider?.type;
  };

  // Helper function to check if provider is salon
  const isSalonProvider = () => {
    return getProviderType() === 'Salon';
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    return endDate.toTimeString().slice(0, 5);
  };

  const calculateGrandTotal = () => {
    let total = 0;
    
    if (isReschedule) {
      // For reschedule, only charge the reschedule fee
      total = rescheduleFee || 0;
    } else {
      // For new bookings, charge the full service amount
      total = subTotal;
    }

    // Platform service fee (1.62% of post-discount service total; label "Service fee" in UI)
    total += serviceFeeAmount;
    
    // Add tip
    total += tipAmount;
    
    // Apply coupon discount
    total -= couponDiscount;
    
    // Apply wallet balance or gems
    if (paymentOption === 'balance') {
      total -= appliedBalance;
    } else if (paymentOption === 'gems') {
      const gemsValue = (appliedGems / 1000) * 10; // 1000 gems = $10
      total -= gemsValue;
    }
    
    setGrandTotal(Math.max(0, total));
  };

  // Handle wallet option changes
  const handleWalletOptionChange = (option) => {
    setPaymentOption(option);
    
    if (option === 'balance') {
      // Apply 80% rule: can use up to 80% of grand total from wallet
      const tempGrandTotal = subTotal + serviceFeeAmount + tipAmount - couponDiscount;
      const maxWalletUsage = tempGrandTotal * 0.8; // 80% of order total
      const amountToApply = Math.min(walletBalance, maxWalletUsage);
      setAppliedBalance(amountToApply);
      setAppliedGems(0);
    } else if (option === 'gems') {
      // Gems: Fixed 1000 gems per booking (not 80% rule)
      // Check if user has at least 1000 gems
      if (walletGems < 1000) {
        setAppliedGems(0);
        setAppliedBalance(0);
        return;
      }
      
      // Apply exactly 1000 gems = discount (amount calculated dynamically)
      setAppliedGems(1000);
      setAppliedBalance(0);
    } else {
      setAppliedBalance(0);
      setAppliedGems(0);
    }
  };

  const handleCouponApply = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      setLoading(true);
      setCouponError(''); // Clear previous errors
      // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/offers/validate-coupon`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/offers/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('customerToken')}`
        },
        body: JSON.stringify({
          couponCode: couponCode.trim(),
          orderAmount: subTotal,
          orderType: 'service'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCouponDiscount(data.data.discountAmount);
          setCouponError(''); // Clear errors on success
        } else {
          setCouponError(data.message || 'Invalid coupon code');
        }
      } else {
        const errorData = await response.json();
        setCouponError(errorData.message || 'Failed to apply coupon');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Failed to apply coupon - please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      const response = await locationAPI.searchLocation(searchQuery);
      setLocationSuggestions(response.data?.suggestions || []);
    } catch (error) {
      console.error('Error searching location:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLocation = (suggestion) => {
    setServiceAddress(suggestion.address);
    setLocationSuggestions([]);
    setSearchQuery('');
  };

  const handleGetCurrentLocation = async () => {
    try {
      setLoading(true);
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by this browser');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const response = await locationAPI.reverseGeocode(latitude, longitude);
          setServiceAddress(response.data?.address || 'Current Location');
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Failed to get current location. Please enter your address manually.');
        }
      );
    } catch (error) {
      alert('Failed to get current location');
    } finally {
      setLoading(false);
    }
  };

  // Check if checkout is allowed (total > 0)
  const isCheckoutAllowed = () => {
    return grandTotal > 0;
  };

  const handlePayment = async () => {
    // Check if checkout is allowed
    if (!isCheckoutAllowed()) {
      setPaymentError(`Order total must be greater than ${formatCurrency(0)}. Please adjust your selections.`);
      return;
    }

    addLog('Secure Payment button clicked', {
      isProcessing,
      grandTotal,
      cartItems: cartItems.length,
      serviceAddress: !!serviceAddress,
      paymentOption,
      appliedBalance,
      appliedGems
    });
    
    addLog('Opening Stripe payment form dialog');
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = async (paymentResult) => {
    setIsProcessing(true);
    
    // Close the payment form dialog first
    setShowPaymentForm(false);
    
    try {
      // Check if this is a reschedule operation
      if (isReschedule && originalBooking) {
        addLog('Processing reschedule booking in payment success');
        
        const rescheduleData = {
          newDate: selectedSlot.date,
          newStartTime: selectedSlot.time,
          newEndTime: selectedSlot.endTime || calculateEndTime(selectedSlot.time, selectedSlot.duration),
          rescheduleFee: rescheduleFee || 0,
          reason: 'Customer requested reschedule',
          specialInstructions: specialInstructions,
          paymentMethod: paymentResult?.isFreeReschedule ? 'free' : (paymentResult?.paymentMethod || 'card'),
          paymentConfirmationId: paymentResult?.isFreeReschedule ? 'FREE_RESCHEDULE' : (paymentResult?.paymentIntentId || paymentResult?.confirmationNumber),
          newReservationId: selectedSlot?.reservationId
        };
        
        if (!originalBooking.id) {
          throw new Error('Original booking ID is missing');
        }
        
        const response = await bookingAPI.rescheduleBooking(originalBooking.id, rescheduleData);
        
       // console.log('🔍 [RESCHEDULE] API response:', response);
        
        if (response.success) {
          addLog('Booking rescheduled successfully', response.data);
          
      // OPTIMIZATION: Move reservation confirmation to background (it's not critical for navigation)
      setTimeout(async () => {
        try {
          if (selectedSlot?.reservationId) {
            await serviceBookingAPI.confirmReservation(selectedSlot.reservationId);
          }
        } catch (error) {
          console.error('Failed to confirm slot reservation for reschedule:', error);
        }
      }, 0);

      // OPTIMIZATION: Move slow operations to background to avoid blocking navigation
      setTimeout(async () => {
        try {
          // Clear cart items for this provider after successful reschedule
          if (provider?.id) {
            await clearProviderCart(provider.id);
          }

          // Stop global timer
          if (timerState.isVisible) {
            stopGlobalTimer();
          }
        } catch (error) {
          console.error('Error in background cleanup after reschedule:', error);
        }
      });
          
          // Navigate to booking success page with reschedule data
          navigate('/booking-success', {
            state: {
              isReschedule: true,
              originalBooking: originalBooking,
              rescheduleData: response.data,
              rescheduleFee: rescheduleFee,
              paymentResult: paymentResult,
              bookingData: {
                bookingNumber: response.data.bookingGroup?.originalBookingNumber || response.data.booking?.bookingNumber,
                paymentConfirmationNumber: paymentResult?.paymentIntentId || paymentResult?.confirmationNumber,
                stripePaymentIntentId: paymentResult?.paymentIntentId,
                confirmationNumber: response.data.bookingGroup?.originalBookingNumber || response.data.booking?.bookingNumber,
                paymentMethod: paymentResult?.paymentMethod || 'card',
                transactionId: paymentResult?.transactionId || Date.now(),
                services: buildServicePayload(),
                teamMember: selectedSlot?.teamMember ? {
                  id: selectedSlot.teamMember.id,
                  name: selectedSlot.teamMember.name,
                  firstName: selectedSlot.teamMember.firstName,
                  lastName: selectedSlot.teamMember.lastName
                } : null,
                providerName: provider?.name,
                providerType: getProviderType(),
                providerAddress: provider?.address || provider?.fullAddress,
                providerPhone: provider?.phone || provider?.phoneNumber,
                date: selectedSlot?.date,
                startTime: selectedSlot?.time,
                duration: selectedSlot?.duration,
                serviceType: !isSalonProvider() ? 'at_home' : 'in_salon',
                serviceAddress: !isSalonProvider() ? serviceAddress : null,
                totalAmount: rescheduleFee || 0,
                subTotal: computeTotalPrice(),
                appliedWalletBalance: 0,
                appliedGems: 0,
                specialInstructions: specialInstructions,
                isReschedule: true,
                originalBooking: originalBooking,
                rescheduleFee: rescheduleFee
              },
              providerData: {
                id: provider?.id,
                name: provider?.name,
                type: provider?.type,
                providerType: provider?.providerType,
                phone: provider?.phone || provider?.phoneNumber,
                address: provider?.address || provider?.fullAddress
              }
            }
          });
          return;
        } else {
          throw new Error(response.error || 'Failed to reschedule booking');
        }
      }
      
      addLog('Payment processing completed, booking should be created by payment endpoint');
      
      const newOrderId = paymentResult?.transactionId || Date.now();
      setCreatedOrderId(newOrderId);
      
      // OPTIMIZATION: Prepare booking data first (minimal processing)
      const bookingData = {
        bookingNumber: paymentResult?.bookingNumber || paymentResult?.orderNumber || `BK-${Date.now()}`,
        paymentConfirmationNumber: paymentResult?.paymentIntentId || paymentResult?.paymentConfirmationNumber,
        stripePaymentIntentId: paymentResult?.paymentIntentId,
        confirmationNumber: paymentResult?.confirmationNumber || `BK-${Date.now()}`,
        paymentMethod: paymentResult?.paymentMethod || 'card',
        transactionId: paymentResult?.transactionId || newOrderId,
        services: buildServicePayload(),
        teamMember: selectedSlot?.teamMember,
        providerName: provider?.name,
        providerType: getProviderType(),
        providerAddress: provider?.address || provider?.fullAddress,
        providerPhone: provider?.phone || provider?.phoneNumber,
        date: selectedSlot?.date,
        startTime: selectedSlot?.time,
        duration: selectedSlot?.duration || computeTotalDuration(),
        serviceType: !isSalonProvider() ? 'at_home' : 'in_salon',
        serviceAddress: !isSalonProvider() ? serviceAddress : null,
        totalAmount: grandTotal,
        subTotal: subTotal,
        serviceFee: serviceFeeAmount,
        tipAmount: tipAmount,
        appliedWalletBalance: appliedBalance,
        appliedGems: appliedGems,
        specialInstructions: specialInstructions
      };
      
          // Navigate immediately to success page
          navigate('/booking-success', { 
            state: { 
              bookingData,
              providerData: provider
            } 
          });
      
      // OPTIMIZATION: ALL cleanup operations in background (don't block navigation)
      setTimeout(async () => {
        try {
          if (selectedSlot?.reservationId) {
            await serviceBookingAPI.confirmReservation(selectedSlot.reservationId);
          }
        } catch (error) {
          console.error('Failed to confirm slot reservation:', error);
        }
      }, 0);

      // OPTIMIZATION: Move slow operations to background to avoid blocking navigation
      setTimeout(async () => {
        try {
          // Clear cart items for this provider after successful booking
          if (provider?.id) {
            await clearProviderCart(provider.id);
          }

          // Stop global timer
          if (timerState.isVisible) {
            stopGlobalTimer();
          }
        } catch (error) {
          console.error('Error in background cleanup:', error);
        }
      });
      
    } catch (error) {
      console.error('❌ Error processing payment:', error);
      addLog('Error processing payment', { error: error.message });
      
      // For demo purposes, if backend fails, still show success
      addLog('Showing demo success due to backend error');
      const newOrderId = paymentResult?.transactionId || Date.now();
      setCreatedOrderId(newOrderId);
      
      // Clear cart items for this provider after successful booking
      if (provider?.id) {
        await clearProviderCart(provider.id);
      }
      
      // Prepare demo booking data for success page
      const demoBookingData = {
        id: newOrderId,
        confirmationNumber: `BK-${newOrderId}`,
        paymentMethod: 'Credit Card',
        transactionId: newOrderId,
        services: buildServicePayload(),
        teamMember: selectedSlot?.teamMember ? {
          id: selectedSlot.teamMember.id,
          name: selectedSlot.teamMember.name,
          firstName: selectedSlot.teamMember.firstName,
          lastName: selectedSlot.teamMember.lastName
        } : null,
        providerName: provider?.name,
        providerType: provider?.type,
        providerAddress: provider?.address || provider?.fullAddress,
        date: selectedSlot?.date,
        startTime: selectedSlot?.time,
        duration: selectedSlot?.duration || computeTotalDuration(),
        serviceType: !isSalonProvider() ? 'at_home' : 'in_salon',
        serviceAddress: !isSalonProvider() ? serviceAddress : null,
        totalAmount: grandTotal,
        subTotal: subTotal,
        serviceFee: serviceFeeAmount,
        tipAmount: tipAmount,
        appliedWalletBalance: appliedBalance,
        appliedGems: appliedGems,
        specialInstructions: specialInstructions
      };
      
      addLog('Navigating to booking success page with demo data', { demoBookingData });
      
      // Navigate to booking success page with demo booking data
      navigate('/booking-success', { 
        state: { 
          bookingData: demoBookingData,
          providerData: provider
        } 
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setIsProcessing(false);
    setShowPaymentForm(false);
    // Convert error to string to avoid React rendering issues
    const errorMessage = error instanceof Error ? error.message : String(error);
    setPaymentError(errorMessage);
    setShowErrorDialog(true);
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setIsProcessing(false);
  };

  const handleCreateBooking = async () => {
    try {
      addLog('Creating booking with data', {
        provider: provider?.id,
        cartItems: cartItems.length,
        selectedSlot,
        isReschedule,
        originalBooking: originalBooking?.id,
        reservationId: selectedSlot?.reservationId
      });

      // Reschedule logic is now handled in handlePaymentSuccess
      if (isReschedule && originalBooking) {
        addLog('Reschedule detected - will be handled in payment success callback');
        return { success: true, isReschedule: true };
      } else {
        // Handle new booking creation
        addLog('Creating new booking');
        
        // If we have a reservation ID, confirm it first
        if (selectedSlot?.reservationId) {
          addLog('Confirming slot reservation', { reservationId: selectedSlot.reservationId });
          
          try {
            const confirmResponse = await serviceBookingAPI.confirmReservation(selectedSlot.reservationId);
            if (!confirmResponse.success) {
              addLog('Failed to confirm reservation', { error: confirmResponse.error });
              throw new Error('Slot reservation expired or invalid');
            }
            addLog('Slot reservation confirmed');
          } catch (error) {
            addLog('Error confirming reservation', { error: error.message });
            throw new Error('Slot reservation expired or invalid');
          }
        }
        
        const finalStaffAllocation = selectedSlot?.staffAllocation || (selectedSlot?.slots && selectedSlot.slots.length > 0 ? selectedSlot.slots[0].staffAllocation : []) || [];
         
        // Additional validation and logging
        if (finalStaffAllocation && Array.isArray(finalStaffAllocation) && finalStaffAllocation.length > 0) {
          console.log('✅ [CHECKOUT] Staff allocation found:', finalStaffAllocation.map((allocation, index) => ({
            index: index + 1,
            serviceId: allocation.serviceId,
            teamMemberId: allocation.teamMemberId || allocation.staffId,
            startTime: allocation.startTime,
            endTime: allocation.endTime
          })));
        } else {
          console.log('⚠️ [CHECKOUT] No valid staff allocation found - this may cause issues in payment processing');
          console.log('🔍 [CHECKOUT] Available data for debugging:', {
            selectedSlot: selectedSlot,
            cartItems: cartItems,
            teamMember: selectedSlot?.teamMember
          });
        }
        
        const servicesForPayload = buildServicesWithColors();

        const bookingData = {
          providerId: provider.id,
          date: selectedSlot.date,
          startTime: selectedSlot.time,
          duration: selectedSlot.duration || computeTotalDuration(),
          services: servicesForPayload,
          // Team member information
          teamMember: selectedSlot.teamMember ? {
            id: selectedSlot.teamMember.id,
            name: selectedSlot.teamMember.name,
            firstName: selectedSlot.teamMember.firstName,
            lastName: selectedSlot.teamMember.lastName
          } : null,
          // Booking type and location
          bookingType: selectedSlot.bookingType,
          serviceType: !isSalonProvider() ? 'at_home' : 'in_salon',
          serviceAddress: !isSalonProvider() ? serviceAddress : null,
          // Payment information
          totalAmount: grandTotal,
          subTotal: subTotal,
          serviceFee: serviceFeeAmount,
          tax: grandTotal - subTotal,
          tipAmount: tipAmount,
          paymentMethod: paymentOption,
          appliedWalletBalance: appliedBalance,
          appliedGems: appliedGems,
          // Additional details
          specialInstructions: specialInstructions,
          reservationId: selectedSlot?.reservationId,
          // Staff allocation for multi-service bookings
          staffAllocation: finalStaffAllocation,
          // Provider information
          providerName: provider.name,
          providerType: provider.type,
          providerAddress: provider.address || provider.fullAddress
        };
        
        addLog('Complete booking data being sent to backend', bookingData);
        console.log('🔍 [CHECKOUT] Full bookingData being sent to payment processing:', JSON.stringify(bookingData, null, 2));
        addLog('Team member information being sent', {
          teamMember: selectedSlot.teamMember,
          teamMemberId: selectedSlot.teamMember?.id,
          teamMemberName: selectedSlot.teamMember?.name,
          teamMemberType: typeof selectedSlot.teamMember,
          teamMemberIdType: typeof selectedSlot.teamMember?.id,
          bookingDataTeamMember: bookingData.teamMember,
          bookingDataTeamMemberId: bookingData.teamMember?.id
        });
        
        addLog('Staff allocation being sent to payment processing', {
          staffAllocation: finalStaffAllocation,
          staffAllocationCount: finalStaffAllocation.length,
          staffAllocationDetails: finalStaffAllocation.map((allocation, index) => ({
            serviceIndex: index + 1,
            serviceId: allocation.serviceId,
            teamMemberId: allocation.teamMemberId || allocation.staffId,
            startTime: allocation.startTime,
            endTime: allocation.endTime,
            duration: `${allocation.startTime} - ${allocation.endTime}`
          })) || []
        });

        addLog('Service information being sent to backend', {
          cartItems: cartItems,
          bookingDataServices: bookingData.services,
          servicesWithColors: bookingData.services.map(service => ({
            serviceId: service.serviceId,
            serviceName: service.serviceName,
            serviceColor: service.color,
            hasColor: !!service.color,
            duration: service.duration,
            price: service.price
          }))
        });
        const response = await serviceBookingAPI.createServiceBooking(bookingData);
        
        if (response.success) {
          addLog('New booking created successfully', response.data);
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to create booking');
        }
      }
    } catch (error) {
      addLog('Error creating/rescheduling booking', { error: error.message });
      throw error;
    }
  };

  const renderCartItems = () => {
    // Debug: Log cart items in reschedule mode
    if (isReschedule) {
 // console.log('🔍 [RESCHEDULE CHECKOUT] Cart items:', cartItems);
 // console.log('🔍 [RESCHEDULE CHECKOUT] Original booking:', {
//    id: originalBooking.id,
//    bookingNumber: originalBooking.bookingNumber,
//    totalAmount: originalBooking.totalAmount,
  //  status: originalBooking.status,
 //   date: originalBooking.date,
  //  startTime: originalBooking.startTime,
 //   endTime: originalBooking.endTime
//  });
    }

    return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon color="primary" />
            {isReschedule ? 'Service Details' : 'Items Added'}
        </Typography>
        
          {cartItems.length > 0 ? (
        <List dense>
          {cartItems.map((item, index) => {
            const quantity = getItemQuantity(item);
            const unitPriceValue = parsePriceValue(item.price);
            const lineTotalValue = unitPriceValue * quantity;

            return (
            <ListItem key={index} sx={{ px: 0, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                {/* Service Photo */}
                <Box
                  component="img"
                  src={item.image || item.photo || 'https://via.placeholder.com/60x60?text=Service'}
                      alt={item.name || item.serviceName}
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: 1, 
                    objectFit: 'cover',
                    border: '1px solid #e0e0e0'
                  }}
                />
                
                {/* Service Details */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {quantity > 1
                      ? `${item.productName || item.serviceName || item.name} x ${quantity}`
                      : item.productName || item.serviceName || item.name}
                  </Typography>
                      
                  
                  {/* Duration with icon */}
                  {item.duration && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <TimeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        {item.duration} min
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    Qty: {quantity}
                  </Typography>
                  {!isReschedule && (
                    <Typography variant="body2" color="text.secondary">
                      Unit: {formatPrice(unitPriceValue)}
                    </Typography>
                  )}
                  
                </Box>
                
                    {/* Price - only show if not in reschedule mode */}
                    {!isReschedule && (
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {formatPrice(lineTotalValue)}
                </Typography>
                    )}
              </Box>
            </ListItem>
            );
          })}
        </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {isReschedule ? 'No service details available' : 'No items in cart'}
              </Typography>
            </Box>
          )}
      </CardContent>
    </Card>
  );
  };

  const renderCheckoutDetails = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Booking Details
        </Typography>
        
        <Grid container spacing={2}>
                     <Grid item xs={12} sm={6}>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
               <BusinessIcon color="primary" fontSize="small" />
               <Typography variant="body2">
                 <strong>Provider Name:</strong> {provider?.name || 'Not available'}
               </Typography>
             </Box>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
               {isSalonProvider() ? (
                 <StoreIcon color="primary" fontSize="small" />
               ) : (
                 <PersonIcon color="primary" fontSize="small" />
               )}
               <Typography variant="body2">
                 <strong>Provider Type:</strong> {getProviderType()}
               </Typography>
             </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                <strong>Contact:</strong> {provider?.phone || '+1234567890'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EventIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                <strong>Date:</strong> {selectedSlot?.date ? formatDateShort(selectedSlot.date) : formatDateShort(new Date())}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimeIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                <strong>Time:</strong> {selectedSlot?.time || '10:00 AM'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HomeIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                <strong>Type:</strong> {isSalonProvider() ? 'In Salon' : 'At Home'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOnIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                <strong>{isSalonProvider() ? 'Address:' : 'Service Address:'}</strong> {
                  isSalonProvider() 
                    ? (provider?.address || 'Salon location will be provided after booking')
                    : (serviceAddress || 'Not selected')
                }
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Show change address option only for Pros (At Home) bookings */}
        {!isSalonProvider() && (
          <Button
            variant="outlined"
            startIcon={<LocationOnIcon />}
            onClick={() => setShowLocationDialog(true)}
            sx={{ mt: 2 }}
          >
            Change Service Address
          </Button>
        )}
      </CardContent>
    </Card>
  );

  const renderSavingCorner = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CouponIcon color="primary" />
          Saving Corner
        </Typography>
        
        {isReschedule ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Coupons and wallet options are not available for reschedule bookings.
          </Alert>
        ) : (
          <>
            {/* Coupon Section */}
            <Card sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              overflow: 'hidden'
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
                p: 2,
                color: 'white'
              }}>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: 600, 
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CouponIcon />
                  Apply Coupon Code
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    sx={{ 
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        background: '#f8f9fa',
                        '&:hover fieldset': {
                          borderColor: '#e91e63',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#e91e63',
                        },
                      },
                    }}
                    disabled={isReschedule}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCouponApply}
                    disabled={loading || !couponCode.trim() || isReschedule}
                    sx={{
                      background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
                      borderRadius: 1.5,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 10px rgba(233, 30, 99, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #c2185b 0%, #e91e63 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 15px rgba(233, 30, 99, 0.4)'
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                        color: '#9e9e9e'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Apply'}
                  </Button>
                </Box>
                {couponDiscount > 0 && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mt: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                      border: '1px solid #c3e6cb',
                      '& .MuiAlert-icon': {
                        color: '#155724'
                      }
                    }}
                    action={
                      <Button
                        size="small"
                        onClick={() => {
                          setCouponCode('');
                          setCouponDiscount(0);
                          setCouponError('');
                        }}
                        sx={{
                          minWidth: 'auto',
                          p: 0.5,
                          color: '#dc3545',
                          fontSize: '0.7rem',
                          '&:hover': {
                            backgroundColor: 'rgba(220, 53, 69, 0.1)'
                          }
                        }}
                      >
                        ✕ Remove
                      </Button>
                    }
                  >
                    🎉 Coupon Discount ({couponCode}): -{formatCurrency(couponDiscount)}
                  </Alert>
                )}
                {couponError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                      border: '1px solid #f5c6cb',
                      '& .MuiAlert-icon': {
                        color: '#721c24'
                      }
                    }}
                  >
                    ❌ {couponError}
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Wallet Options */}
            <Card sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              overflow: 'hidden'
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                p: 2,
                color: 'white'
              }}>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: 600, 
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <WalletIcon />
                  Wallet Options
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  value={paymentOption}
                  onChange={(e) => {
                    handleWalletOptionChange(e.target.value);
                  }}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  <FormControlLabel 
                    value="none" 
                    control={
                      <Radio 
                        onClick={() => {
                          handleWalletOptionChange('none');
                        }}
                        sx={{
                          '&.Mui-checked': {
                            color: '#00bcd4',
                          },
                        }}
                      />
                    } 
                    label={
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0.5,
                        flex: 1,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: paymentOption === 'none' ? '#f8f9fa' : 'transparent',
                        border: paymentOption === 'none' ? '2px solid #00bcd4' : '2px solid transparent',
                        borderColor: paymentOption === 'none' ? '#00bcd4' : 'transparent',
                        background: paymentOption === 'none' ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : 'transparent'
                      }}>
                        <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                          None
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Pay with card only
                        </Typography>
                      </Box>
                    }
                    sx={{ 
                      flex: 1,
                      m: 0,
                      '& .MuiFormControlLabel-label': {
                        width: '100%'
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5
                  }}>
                    <FormControlLabel 
                      value="balance" 
                      control={
                        <Radio 
                          onClick={() => {
                            handleWalletOptionChange('balance');
                          }}
                          sx={{
                            '&.Mui-checked': {
                              color: '#00bcd4',
                            },
                          }}
                        />
                      } 
                      label={
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          flex: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: paymentOption === 'balance' ? '#e8f5e8' : 'transparent',
                          border: paymentOption === 'balance' ? '2px solid #4caf50' : '2px solid transparent'
                        }}>
                          <WalletIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Balance {formatCurrency(walletBalance)}
                          </Typography>
                        </Box>
                      }
                      disabled={walletBalance <= 0 || isReschedule}
                      sx={{ 
                        flex: 1,
                        m: 0,
                        '& .MuiFormControlLabel-label': {
                          width: '100%'
                        }
                      }}
                    />
                    <FormControlLabel 
                      value="gems" 
                      control={
                        <Radio 
                          onClick={() => {
                            handleWalletOptionChange('gems');
                          }}
                          sx={{
                            '&.Mui-checked': {
                              color: '#00bcd4',
                            },
                          }}
                        />
                      } 
                      label={
                        <Box 
                          onClick={() => {
                            if (walletGems >= 1000) {
                              handleWalletOptionChange('gems');
                            }
                          }}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            flex: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: paymentOption === 'gems' ? '#fff3e0' : 'transparent',
                            border: paymentOption === 'gems' ? '2px solid #ff9800' : '2px solid transparent',
                            cursor: walletGems >= 1000 ? 'pointer' : 'not-allowed',
                            opacity: walletGems >= 1000 ? 1 : 0.5
                          }}>
                          <DiamondIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Gems {walletGems}
                          </Typography>
                          {walletGems >= 1000 && (
                            <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.7rem' }}>
                              💎 1000 gems = {formatCurrency(10)} discount
                            </Typography>
                          )}
                        </Box>
                      }
                      disabled={walletGems < 1000}
                      sx={{ 
                        flex: 1,
                        m: 0,
                        '& .MuiFormControlLabel-label': {
                          width: '100%'
                        }
                      }}
                    />
                  </Box>
                </RadioGroup>
              </FormControl>
                {(walletBalance <= 0 && walletGems <= 0) && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                    border: '1px solid #ffeaa7'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#856404',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}>
                      No wallet balance or gems available
                    </Typography>
                  </Box>
                )}
                {walletGems > 0 && walletGems < 1000 && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                    border: '1px solid #ffeaa7'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#856404',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      fontSize: '0.75rem'
                    }}>
                      💎 You need at least 1000 gems to use them (1000 gems = {formatCurrency(10)} discount)
                    </Typography>
                  </Box>
                )}
                {(walletBalance > 0 || walletGems >= 1000) && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)',
                    border: '1px solid #bee5eb'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#0c5460',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      fontSize: '0.75rem'
                    }}>
                      💡 Wallet: Up to 80% of order total | Gems: Fixed 1000 gems = {formatCurrency(10)} discount
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}

      </CardContent>
    </Card>
  );

  const renderTipAndInstructions = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" />
          Tip & Instructions
        </Typography>
        
        {isReschedule ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Tips are not available for reschedule bookings.
          </Alert>
        ) : (
          /* Tip Options */
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add Tip
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tipOptions.map((tip) => (
                <Chip
                  key={tip}
                  label={formatCurrency(tip)}
                  onClick={() => setTipAmount(tip)}
                  color={tipAmount === tip ? "primary" : "default"}
                  variant={tipAmount === tip ? "filled" : "outlined"}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Special Instructions */}
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Special instructions (optional)"
          value={specialInstructions}
          onChange={(e) => setSpecialInstructions(e.target.value)}
        />
      </CardContent>
    </Card>
  );

  const renderBillSummary = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Bill Summary
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          {!isReschedule && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Sub Total</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(subTotal)}
              </Typography>
            </Box>
          )}
          
          {isReschedule && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Reschedule Fee</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: rescheduleFee > 0 ? 'error.main' : 'success.main' }}>
                {rescheduleFee > 0 ? formatCurrency(rescheduleFee) : 'Free'}
              </Typography>
            </Box>
          )}
          
          {couponDiscount > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mb: 1.5, 
              p: 1.5,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
              border: '1px solid #c3e6cb'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#155724', fontSize: '0.85rem' }}>
                  🎉 Coupon Discount ({couponCode})
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setCouponCode('');
                    setCouponDiscount(0);
                    setCouponError('');
                  }}
                  sx={{
                    minWidth: 'auto',
                    p: 0.5,
                    color: '#dc3545',
                    fontSize: '0.7rem',
                    '&:hover': {
                      backgroundColor: 'rgba(220, 53, 69, 0.1)'
                    }
                  }}
                >
                  ✕ Remove
                </Button>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#155724', fontSize: '0.85rem' }}>
                -{formatCurrency(couponDiscount)}
              </Typography>
            </Box>
          )}

          {(serviceFeeAmount ?? 0) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Service fee</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(serviceFeeAmount)}
              </Typography>
            </Box>
          )}

          {!isReschedule && tipAmount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Tip Amount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(tipAmount)}
              </Typography>
            </Box>
          )}
          
          {appliedBalance > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">💳 Wallet Applied</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                -{formatCurrency(appliedBalance)}
              </Typography>
            </Box>
          )}
          
          {appliedGems > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">💎 Gems Applied ({appliedGems} gems)</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                -{formatCurrency((appliedGems / 1000) * 10)}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Grand Total
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {formatCurrency(grandTotal)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  // Enhanced Location Dialog
  const renderLocationDialog = () => (
    <EnhancedLocationDialog
      open={showLocationDialog}
      onClose={() => setShowLocationDialog(false)}
      onLocationSelect={(locationData) => {
        setServiceAddress(locationData.address);
        
        // Save location data to localStorage
        const locationInfo = {
          address: locationData.address,
          fullAddress: locationData.address,
          latitude: locationData.lat,
          longitude: locationData.lng,
          city: locationData.address ? locationData.address.split(',')[0] : '', // Extract city from address with null check
          type: locationData.type
        };
        localStorage.setItem('userLocation', JSON.stringify(locationInfo));
        
        setShowLocationDialog(false);
      }}
    />
  );

  // Timer Expiration Dialog
  const renderTimerExpiredDialog = () => (
    <Dialog 
      open={showTimerExpiredDialog} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        <AccessTime sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
          Slot Reservation Expired
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Your slot reservation has expired. You will be redirected to select a new time slot.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Redirecting in 3 seconds...
          </Typography>
        <CircularProgress size={24} />
      </DialogContent>
    </Dialog>
  );


  if (!provider || !cartItems || !selectedSlot) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          No checkout data available
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/home')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      p: 3,
      pb: { xs: 10, sm: 3 } // Extra padding at bottom for mobile to account for bottom navigation
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        backgroundColor: '#1a237e',
        color: 'white',
        p: 2,
        borderRadius: 1,
        boxShadow: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mr: 2, color: 'white' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
          {isReschedule ? 'Reschedule Booking' : 'Checkout'}
        </Typography>
      </Box>
      </Box>

      {/* Timer Expiration Alert */}
      {!isReschedule && !isProcessing && !timerState.isVisible && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Slot Reservation Expired
          </Typography>
          <Typography variant="body2">
            Your slot reservation has expired. You will be redirected to select a new time slot.
          </Typography>
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, maxWidth: '100%' }}>
        {/* Left Column - Order Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {renderCartItems()}
          {renderCheckoutDetails()}
          {renderSavingCorner()}
          {renderTipAndInstructions()}
        </Box>

        {/* Right Column - Bill Summary and Payment */}
        <Box sx={{ width: { xs: '100%', lg: 400 }, flexShrink: 0 }}>
          {renderBillSummary()}
          
          {/* Terms and Conditions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                By proceeding, you agree to our{' '}
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate('/terms')}
                >
                  Terms & Conditions
                </Button>
                {' '}and{' '}
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate('/cancellation-policy')}
                >
                  Service Cancellation Policy
                </Button>
              </Typography>
            </CardContent>
          </Card>

          {/* Order Total Warning */}
          {!isCheckoutAllowed() && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Order total must be greater than {formatCurrency(0)}. Please adjust your selections or remove discounts.
              </Typography>
            </Alert>
          )}

          {/* Payment Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
            onClick={handlePayment}
            disabled={!serviceAddress || (!isReschedule && !timerState.isVisible) || !isCheckoutAllowed()}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              backgroundColor: '#1a237e',
              mb: { xs: 3, sm: 0 }, // Extra margin at bottom for mobile for better visibility
              '&:hover': {
                backgroundColor: '#0d47a1'
              }
            }}
          >
            {isProcessing ? 'Processing...' : `${isReschedule ? (rescheduleFee > 0 ? 'Reschedule' : 'Confirm Reschedule') : 'Secure Payment'} ${isReschedule && rescheduleFee === 0 ? '' : formatCurrency(grandTotal)}`}
          </Button>
        </Box>
      </Box>

      {/* Stripe Payment Form Dialog */}
      <Dialog
        open={showPaymentForm}
        onClose={handlePaymentCancel}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'none'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            💳 Secure Payment with Stripe
          </Typography>
          <IconButton
            onClick={handlePaymentCancel}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {grandTotal > 0 && stripePromise ? (
            <Elements 
              stripe={stripePromise}
              options={{
                mode: 'payment',
                amount: Math.round(grandTotal * 100), // Convert to cents
                currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
                paymentMethodCreation: 'manual', // Required for createPaymentMethod
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#3b82f6',
                    colorBackground: '#ffffff',
                    colorText: '#1e293b',
                    colorDanger: '#ef4444',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                  }
                }
              }}
            >
              <PaymentForm
            amount={grandTotal}
                grandTotal={grandTotal}
                subTotal={subTotal}
                serviceFeeAmount={serviceFeeAmount}
                tipAmount={tipAmount}
                couponCode={couponCode}
                couponDiscount={couponDiscount}
                appliedBalance={appliedBalance}
                appliedGems={appliedGems}
                savedCards={savedCards}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                bookingData={{
                  providerId: provider?.id,
                  date: selectedSlot?.date,
                  startTime: selectedSlot?.time,
                  duration: selectedSlot?.duration || computeTotalDuration(),
                  services: buildServicesWithColors(),
                  teamMember: selectedSlot?.teamMember ? {
                    id: selectedSlot.teamMember.id,
                    name: selectedSlot.teamMember.name,
                    firstName: selectedSlot.teamMember.firstName,
                    lastName: selectedSlot.teamMember.lastName
                  } : null,
                  // Include staffAllocation for multi-service bookings
                  staffAllocation: selectedSlot?.staffAllocation || [],
                  bookingType: selectedSlot?.bookingType,
                  serviceType: !isSalonProvider() ? 'at_home' : 'in_salon',
                  serviceAddress: !isSalonProvider() ? serviceAddress : null,
                  totalAmount: grandTotal,
                  subTotal: subTotal,
                  serviceFee: serviceFeeAmount,
                  tax: grandTotal - subTotal,
                  tipAmount: tipAmount,
                  paymentMethod: paymentOption,
                  appliedWalletBalance: appliedBalance,
                  appliedGems: appliedGems,
                  specialInstructions: specialInstructions,
                  reservationId: selectedSlot?.reservationId,
                  providerName: provider?.name,
                  providerType: provider?.type,
                  providerAddress: provider?.address || provider?.fullAddress,
                  rescheduleFee: isReschedule ? rescheduleFee : undefined,
                  isReschedule: !!isReschedule
                }}
                isReschedule={isReschedule}
                originalBooking={originalBooking}
                onSuccess={(result) => {
                  console.log('Payment successful:', result);
                  setShowPaymentForm(false);
                  handlePaymentSuccess(result);
                }}
                onError={(error) => {
                  console.error('Payment failed:', error);
                  // Convert error to string to avoid React rendering issues
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  setPaymentError(errorMessage);
                  setShowErrorDialog(true);
                }}
                onCancel={() => {
                  setShowPaymentForm(false);
                }}
              />
            </Elements>
          ) : grandTotal === 0 ? (
            // Free reschedule - no payment required
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                Free Reschedule
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No payment required for this reschedule. Click below to confirm your new booking time.
              </Typography>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  // Handle free reschedule directly
                  setShowPaymentForm(false);
                  handlePaymentSuccess({ 
                    success: true, 
                    isFreeReschedule: true,
                    message: 'Reschedule completed successfully'
                  });
                }}
                startIcon={<CheckCircleIcon />}
                sx={{ mr: 2 }}
              >
                Confirm Free Reschedule
              </Button>
              <Button
                variant="outlined"
                onClick={handlePaymentCancel}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                Payment Processing Unavailable
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Stripe payment processing is not configured. Please contact support or try again later.
              </Typography>
              <Button
                variant="outlined"
                onClick={handlePaymentCancel}
                startIcon={<CreditCardIcon />}
              >
                Close
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Error Dialog */}
      <Dialog 
        open={showErrorDialog} 
        onClose={() => {
          setShowErrorDialog(false);
          setPaymentError(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            ⚠️ Payment Error
          </Typography>
          <IconButton
            onClick={() => {
              setShowErrorDialog(false);
              setPaymentError(null);
            }}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ef4444', fontWeight: 600 }}>
              Payment Failed
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#374151', lineHeight: 1.6 }}>
              {paymentError || 'An unexpected error occurred during payment processing.'}
            </Typography>
            
            {/* Common Error Solutions */}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: 2,
              border: '1px solid #f59e0b'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400e', mb: 1 }}>
                💡 Common Solutions:
              </Typography>
              <Typography variant="body2" sx={{ color: '#92400e', textAlign: 'left' }}>
                • Check your card details and ensure they are correct<br/>
                • Verify your card has sufficient funds<br/>
                • Try a different payment method<br/>
                • Contact your bank if the issue persists<br/>
                • Ensure your billing address matches your card
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              setShowErrorDialog(false);
              setPaymentError(null);
            }}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
              }
            }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setShowErrorDialog(false);
              setPaymentError(null);
              setShowPaymentForm(false);
            }}
            sx={{
              borderColor: '#6b7280',
              color: '#6b7280',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#374151',
                color: '#374151',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals */}
      {renderLocationDialog()}
      {renderTimerExpiredDialog()}
      
      {/* Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};

export default Checkout; 